import _ from 'lodash'
import { DialogSchema } from '@feedyou-ai/feedbot-schema'
import { isNotEmptyElement, removeArrayDuplicates, toFirstLetterLowerCase } from '@feedyou/utils'

import { getVariablesInsideCurlyBrackets } from '../../../lib/storage'
import { memoizeByRefAndId } from '../../../lib/performance/memoizeByRefAndId'
import { Storage } from '../types'
import { getTextsThatSupportInterpolatingVariables } from './getTextsThatSupportInterpolatingVariables'

export const extractUserStorages = (dialogs: DialogSchema[]): Storage[] => {
  const userStorageNames = extractUserStorageNames(dialogs)
  const usageCounts = _.countBy(userStorageNames)

  return removeArrayDuplicates(userStorageNames).map(storage => ({
    name: toFirstLetterLowerCase(storage),
    numberOfUses: usageCounts[storage]!,
    storageType: ['user']
  }))
}

function extractUserStorageNames(dialogs: DialogSchema[]): string[] {
  return dialogs
    .map(getUserStorageNamesForDialog)
    .flat() // I <3 PRETTIER
    .filter(isNotEmptyElement)
}

const getUserStorageNamesForDialog = memoizeByRefAndId((dialog: DialogSchema): string[] => {
  const directStorages = getDirectStoragesForDialog(dialog)

  const storagesInterpolatedInStepTexts = dialog.steps
    .map(getTextsThatSupportInterpolatingVariables.forStep)
    .flat()
    .filter(isNotEmptyElement)
    .map(getVariablesInsideCurlyBrackets)
    .flat()

  return [directStorages, storagesInterpolatedInStepTexts].flat()
})

function getDirectStoragesForDialog(dialog: DialogSchema): string[] {
  const stepStorages = dialog.steps?.map(step => step.storage).flat() || []

  const questionStorages = dialog.steps
    .map(step => step?.storages)
    .flat()
    .filter(isNotEmptyElement)
    .map(storage => storage?.storage)

  const answerStorages = dialog.steps
    .map(step => step?.answers)
    .flat()
    .filter(isNotEmptyElement)
    .map(answer => answer?.storages)
    .filter(isNotEmptyElement)
    .flat()
    .map(storage => storage?.storage)

  return [stepStorages, questionStorages, answerStorages].flat().filter(isNotEmptyElement)
}
